import {Directive, Input, numberAttribute} from '@angular/core';
import {AbstractControl, AsyncValidator, NG_ASYNC_VALIDATORS, ValidationErrors, Validators} from '@angular/forms';
import {Observable, of} from 'rxjs';
import {debounceTime, first, map} from 'rxjs/operators';

/**
 * Validate a pattern asynchronously.
 *
 * Usage: <input type="text" nlrPattern="RegExp | string" nlrPatternDebounceTime="number">
 *
 * Usage examples:
 *
 * 1. Validate pattern after default debounce time:
 *
 *    <input type="text" nlrPattern="^[A-Z][0-9]{6}$">
 *
 * 2. Validate pattern after custom debounce time:
 *
 *    <input type="text" nlrPattern="^[A-Z][0-9]{6}$" nlrPatternDebounceTime="500">
 */
@Directive({
    selector: '[nlrPattern]',
    standalone: true,
    providers: [{provide: NG_ASYNC_VALIDATORS, useExisting: NlrPatternDirective, multi: true}]
})
export class NlrPatternDirective implements AsyncValidator {
    @Input()
    nlrPattern: RegExp | string;

    @Input({transform: numberAttribute})
    nlrPatternDebounceTime: number = 750;

    constructor() {}

    validate(control: AbstractControl): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> {
        // When valid, return immediately
        if (Validators.pattern(this.nlrPattern)(control) === null) {
            return of(null);
        }

        // When invalid, validate again after debounce time has elapsed
        return control.valueChanges.pipe(
            debounceTime(this.nlrPatternDebounceTime),
            map(() => (Validators.pattern(this.nlrPattern)(control) ? {nlrPattern: true} : null)),
            first()
        );
    }
}
